// Local-first fast session log. Persists in localStorage and, when
// the user has a Supabase identity (anonymous or permanent), syncs
// to the fast_sessions table. Local cache is the source of truth
// for the UI so the app remains instant and works offline.

import { supabase } from "@/integrations/supabase/client";

export type FastSession = {
  id: string;
  startedAt: number; // ms epoch
  endedAt: number;   // ms epoch
  manuallyEdited?: boolean;
  createdAt: number;
};

const LOG_KEY = "monolith.fast.log";

function read(): FastSession[] {
  try {
    const raw = localStorage.getItem(LOG_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function write(list: FastSession[]) {
  localStorage.setItem(LOG_KEY, JSON.stringify(list));
  window.dispatchEvent(new Event("monolith:log-changed"));
}

// ---------- Supabase sync ----------

async function getUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.user?.id ?? null;
}

async function pushSession(s: FastSession) {
  const uid = await getUserId();
  if (!uid) return;
  try {
    await supabase.from("fast_sessions").upsert({
      id: s.id,
      user_id: uid,
      started_at: new Date(s.startedAt).toISOString(),
      ended_at: new Date(s.endedAt).toISOString(),
      manually_edited: !!s.manuallyEdited,
      source: s.manuallyEdited ? "manual" : "timer",
    });
  } catch (e) {
    console.warn("[fastLog] push failed", e);
  }
}

async function deleteRemote(id: string) {
  const uid = await getUserId();
  if (!uid) return;
  try {
    await supabase.from("fast_sessions").delete().eq("id", id);
  } catch (e) {
    console.warn("[fastLog] delete failed", e);
  }
}

/**
 * Pull remote → merge into local cache (remote wins on conflict).
 * Then push any purely-local sessions up to remote.
 */
export async function syncFastLog(): Promise<void> {
  const uid = await getUserId();
  if (!uid) return;
  try {
    const { data, error } = await supabase
      .from("fast_sessions")
      .select("id, started_at, ended_at, manually_edited, created_at")
      .eq("user_id", uid);
    if (error) throw error;

    const local = read();
    const remoteMap = new Map<string, FastSession>();
    for (const r of data ?? []) {
      remoteMap.set(r.id, {
        id: r.id,
        startedAt: new Date(r.started_at).getTime(),
        endedAt: r.ended_at ? new Date(r.ended_at).getTime() : 0,
        manuallyEdited: !!r.manually_edited,
        createdAt: r.created_at ? new Date(r.created_at).getTime() : Date.now(),
      });
    }

    // Local-only sessions (not in remote) → push
    const localOnly = local.filter((s) => !remoteMap.has(s.id));

    // Merge: remote rows + any local-only
    const merged = [
      ...Array.from(remoteMap.values()),
      ...localOnly,
    ].filter((s) => s.endedAt > s.startedAt);
    write(merged);

    for (const s of localOnly) await pushSession(s);
  } catch (e) {
    console.warn("[fastLog] sync failed", e);
  }
}

// Re-sync whenever auth changes (guest → user, user → other user)
supabase.auth.onAuthStateChange((_event, _session) => {
  // Defer so any in-flight mutation finishes first
  setTimeout(() => { syncFastLog(); }, 0);
});

// ---------- Live cross-device sync ----------

let fastLogChannelBound = false;

/**
 * Wire realtime + visibility listeners exactly once so the completed-session
 * log stays in sync across devices without requiring a full reload.
 */
export function bindFastLogSync() {
  if (fastLogChannelBound) return;
  fastLogChannelBound = true;

  const rebind = async () => {
    const uid = await getUserId();
    if (!uid) return;
    supabase
      .channel(`fast-log-${uid}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "fast_sessions",
          filter: `user_id=eq.${uid}`,
        },
        () => {
          // Any change to this user's sessions → re-pull and merge.
          syncFastLog();
        }
      )
      .subscribe();
  };

  // Initial pull + subscribe on boot (once auth is restored).
  syncFastLog().then(rebind);

  // Refresh when tab becomes visible (handles offline → online and
  // "ended on another device while this one was backgrounded").
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") syncFastLog();
  });
}

// ---------- Public API ----------

export const FastLog = {
  all(): FastSession[] {
    return read().sort((a, b) => a.startedAt - b.startedAt);
  },

  /**
   * Returns the first stored session whose range overlaps [startMs, endMs).
   * Excludes the optional excludeId (use when editing an existing entry).
   */
  findOverlap(startMs: number, endMs: number, excludeId?: string): FastSession | null {
    for (const s of read()) {
      if (excludeId && s.id === excludeId) continue;
      if (s.endedAt <= s.startedAt) continue;
      if (s.startedAt < endMs && s.endedAt > startMs) return s;
    }
    return null;
  },

  add(session: Omit<FastSession, "id" | "createdAt">): FastSession {
    const s: FastSession = {
      id: crypto.randomUUID(),
      createdAt: Date.now(),
      ...session,
    };
    const list = read();
    list.push(s);
    write(list);
    pushSession(s);
    return s;
  },

  /**
   * Insert a completed session into the local log using a caller-supplied id,
   * without pushing to Supabase. Use when the remote row already exists
   * (e.g. the active-fast row was just closed by setting ended_at).
   */
  recordCompletedLocal(session: FastSession): void {
    const list = read();
    if (list.some((s) => s.id === session.id)) return;
    list.push(session);
    write(list);
  },

  update(id: string, patch: Partial<FastSession>): void {
    let updated: FastSession | undefined;
    const list = read().map((s) => {
      if (s.id !== id) return s;
      updated = { ...s, ...patch, manuallyEdited: true };
      return updated;
    });
    write(list);
    if (updated) pushSession(updated);
  },

  remove(id: string): void {
    write(read().filter((s) => s.id !== id));
    deleteRemote(id);
  },

  dayKey(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  },

  /**
   * Attribute the whole fast duration to the local calendar day it ENDED on.
   * No splitting across midnight — a fast that ends at 09:00 Tuesday counts
   * entirely on Tuesday, even if it started Monday night.
   */
  minutesByDay(sessions: FastSession[]): Record<string, number> {
    const out: Record<string, number> = {};
    for (const s of sessions) {
      if (s.endedAt <= s.startedAt) continue;
      const minutes = (s.endedAt - s.startedAt) / 60000;
      const key = FastLog.dayKey(new Date(s.endedAt));
      out[key] = (out[key] || 0) + minutes;
    }
    return out;
  },

  /**
   * A session "belongs to" the local day of its endedAt timestamp.
   */
  sessionsForDay(sessions: FastSession[], day: Date): FastSession[] {
    const key = FastLog.dayKey(day);
    return sessions.filter(
      (s) => s.endedAt > s.startedAt && FastLog.dayKey(new Date(s.endedAt)) === key
    );
  },
};
